/* eslint-disable max-lines */
import {
  fetchRenderedPrompts,
  setSelectedIdentifierOptionsConfig,
  setVariable,
} from 'actions/identifier';
import LoadingIndicator from 'components/LoadingIndicator';
import TermsInput from 'components/TermsInput';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  getCompareMode,
  getProductionIdentifierRevision,
  getRenderedPrompt,
  getRenderedPromptLoading,
  getSelectedCustomerIdentifier,
  getSelectedIdentifierRevision,
  getShouldDisableEdit,
} from 'selectors/identifier';
import { useSelector } from 'store';
import CategoryThresholdInput from './CategoryThresholdInput';

type LanguageMatcherType = {
  isSaved: boolean;
};

const WorkspaceThresholdManagement: React.FC<LanguageMatcherType> = () => {
  const dispatch = useDispatch();

  const identifierRevision = useSelector(getSelectedIdentifierRevision);
  const prodRevision = useSelector(getProductionIdentifierRevision);
  const compareMode = useSelector(getCompareMode);
  const shouldDisable = useSelector(getShouldDisableEdit);
  const selectedCustomer = useSelector(getSelectedCustomerIdentifier);
  const loadingPrompt = useSelector(getRenderedPromptLoading);
  const renderedPrompt = useSelector(getRenderedPrompt);

  useEffect(() => {
    dispatch(fetchRenderedPrompts({ customerId: selectedCustomer }));
  }, [dispatch, selectedCustomer]);

  if (!identifierRevision?.options_config) {
    return null;
  }

  const {
    customers_teams: customersTeamsObject,
    not_customers_teams: notCustomersTeamsObject,
    categorization_facets: categories,
    customer_thresholds: customersThresh,
    variables = [],
    customers_variables: customersVars,
    // @ts-ignore
  } = identifierRevision?.options_config as {
    categorization_facets: string[];
    customer_thresholds: Record<string, Record<string, string>>;
    variables: string[];
    customers_variables: Record<string, Record<string, string>>;
    customers_teams: Record<string, string[]>;
    not_customers_teams: Record<string, string[]>;
  };
  const customerThresholds = customersThresh?.[selectedCustomer];
  const customersVariables = customersVars?.[selectedCustomer];
  const customerTeams = customersTeamsObject?.[selectedCustomer] || [];
  const notCustomerTeams = notCustomersTeamsObject?.[selectedCustomer] || [];

  const {
    customer_thresholds: prodCustomersThresh,
    customers_variables: prodCustomerVars,
    // @ts-ignore
  } = (prodRevision?.options_config || identifierRevision?.options_config) as {
    categorization_facets: string[];
    variables: string[];
    customers_variables: Record<string, Record<string, string>>;
    customer_thresholds: Record<string, Record<string, string>>;
  };
  const prodCustomerThresholds = prodCustomersThresh?.[selectedCustomer];
  const prodCustomersVariables = prodCustomerVars?.[selectedCustomer];

  const addTeam = (value: string): void => {
    if (value && !customerTeams.includes(value)) {
      const config = {
        ...identifierRevision.options_config,
        customers_teams: { ...customersTeamsObject, [selectedCustomer]: [...customerTeams, value] },
      };
      // @ts-ignore
      dispatch(setSelectedIdentifierOptionsConfig(config));
    }
  };

  const deleteTeam = (value: string): void => {
    if (customerTeams.includes(value)) {
      const config = {
        ...identifierRevision.options_config,
        customers_teams: {
          ...customersTeamsObject,
          [selectedCustomer]: customerTeams.filter((c) => c !== value),
        },
      };
      // @ts-ignore
      dispatch(setSelectedIdentifierOptionsConfig(config));
    }
  };

  const addNotTeam = (value: string): void => {
    if (value && !notCustomerTeams.includes(value)) {
      const config = {
        ...identifierRevision.options_config,
        not_customers_teams: {
          ...notCustomersTeamsObject,
          [selectedCustomer]: [...notCustomerTeams, value],
        },
      };
      // @ts-ignore
      dispatch(setSelectedIdentifierOptionsConfig(config));
    }
  };

  const deleteNotTeam = (value: string): void => {
    if (notCustomerTeams.includes(value)) {
      const config = {
        ...identifierRevision.options_config,
        not_customers_teams: {
          ...notCustomersTeamsObject,
          [selectedCustomer]: notCustomerTeams.filter((c) => c !== value),
        },
      };
      // @ts-ignore
      dispatch(setSelectedIdentifierOptionsConfig(config));
    }
  };

  const handleChangeVariable = (e: React.ChangeEvent<HTMLInputElement>, variable: string): void => {
    const { value } = e.target;

    if (value) {
      dispatch(setVariable({ variable, value }));
    } else {
      dispatch(setVariable({ variable, value }));
    }
  };

  return (
    <div className="w-full flex flex-col gap-4 pt-6 pb-10 px-8 text-body overflow-auto">
      <div className="flex flex-col gap-6">
        {/* <div className="flex flex-row gap-2 items-center">
          <ToggleButton
            isOn
            handleToggleClick={(): void => {
              // noop
            }}
          />
          <p>Workspace scope</p>
        </div> */}
        <div className="flex flex-col text-body gap-4">
          <span className="font-bold">Score threshold [0.0 - 1.0]</span>
          <div className="flex flex-row flex-wrap gap-x-6 gap-y-4">
            {categories.map((cat) => {
              const categoryThreshold = customerThresholds?.[cat]?.toString() || '';
              const prodCategoryThreshold = prodCustomerThresholds?.[cat]?.toString() || '';

              return (
                <CategoryThresholdInput
                  key={`${selectedCustomer}-${cat}`}
                  category={cat}
                  categoryThreshold={categoryThreshold}
                  prodCategoryThreshold={prodCategoryThreshold}
                />
              );
            })}
          </div>
        </div>

        {variables && variables.length > 0 && (
          <div className="flex flex-col text-body gap-4">
            <span className="font-bold">Variables</span>
            <div className="flex flex-row flex-wrap gap-x-6 gap-y-4">
              {variables.map((v) => {
                const variableValue = customersVariables?.[v] || '';
                const prodVariableValue = prodCustomersVariables?.[v] || '';

                return (
                  <label key={v} htmlFor={v} className="flex flex-col gap-1 text-body w-50">
                    <div className="flex flex-row">
                      <span>{v}</span>
                      <span className="text-litlingo-alert">*</span>
                    </div>
                    <input
                      name={v}
                      id={v}
                      type="text"
                      data-testid={`${v}-variable-input`}
                      value={variableValue}
                      className={`form-input w-full h-9 py-2 px-4 border border-litlingo-gray-2 rounded-lg text-body focus:outline-none ${
                        prodVariableValue !== variableValue && compareMode
                          ? 'bg-litlingo-focus-area-background'
                          : ''
                      }`}
                      onChange={(e): void => handleChangeVariable(e, v)}
                      disabled={compareMode || shouldDisable}
                    />
                  </label>
                );
              })}
            </div>
          </div>
        )}
        <div className="mt-2 flex flex-row flex-wrap gap-8">
          <div className="flex w-104">
            <TermsInput
              inputName="Enable for users in Team (UUIDs)"
              onChange={addTeam}
              onDelete={deleteTeam}
              terms={customerTeams}
              required={false}
            />
          </div>
          <div className="flex w-104">
            <TermsInput
              inputName="Disable for users in Team (UUIDs)"
              onChange={addNotTeam}
              onDelete={deleteNotTeam}
              terms={notCustomerTeams}
              required={false}
            />
          </div>
        </div>
      </div>
      <div className="mt-2 flex flex-col gap-4">
        <span className="font-bold">Prompt:</span>
        {loadingPrompt && !renderedPrompt && (
          <div className="mt-10 w-full flex flex-row justify-center">
            <LoadingIndicator size="20" />
          </div>
        )}
        {renderedPrompt ? (
          <span className="text-body whitespace-pre-line">{renderedPrompt}</span>
        ) : (
          <span>No rendered prompt</span>
        )}
      </div>
    </div>
  );
};

export default WorkspaceThresholdManagement;
